// Copyright (C) 2022, Rutio AB, All rights reserved

/**
 * TimeSpace — Leaflet map view for displaying device positions on a geographic map.
 *
 * Renders device positions as markers on an OpenStreetMap tile layer using Leaflet.
 * Supports age-based opacity (older positions fade out), zoom tracking, accuracy
 * circles, and custom marker factories for different device states (alarm, trace, etc.).
 *
 * ## Key Behaviors
 * - Filters out items without valid GPS coordinates
 * - Auto-centers on the most recent position if no center prop is given
 * - Calculates opacity range from oldest to newest position (configurable via maxAge)
 * - Handles map resize and container sizing edge cases with multiple invalidateSize calls
 *
 * @module TimeSpace
 */

import L from 'leaflet';
import type { Map as LeafletMap } from 'leaflet';
import React, { useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, useMapEvents } from 'react-leaflet';

import { getAreaBounds } from './components/map/GeofenceAreas/shapeBuilders';
import type { TimeSpaceProps, BaseMapItem, LatLng } from './types';
import logger from './utils/logger';

const ALARM_ZOOM = 18;

/**
 * Map view component for displaying device positions geographically.
 *
 * @param props - TimeSpaceProps with series data, dimensions, zoom, markerFactory, etc.
 * @returns Leaflet map with device markers, or placeholder if no data
 */
export const TimeSpace: React.FC<TimeSpaceProps> = (props) => {
  const height = props.height ? props.height : '30vmin';
  const width = props.width ? props.width : '100vw';
  const top = props.top ? props.top : 0;

  // Ensure minimum dimensions for proper map rendering
  const minHeight = height === '30vmin' ? '300px' : height;
  const minWidth = width === '100vw' ? '100%' : width;
  let series: BaseMapItem[] = props.series ? props.series : [];
  const nowS = new Date().getTime() / 1000;
  let ageForMaxOpacityH = 14 * 24;
  const maxOpacity = 1.0;
  const opacityScale = 0.6; // how much does opacity scale
  const zoom = props.zoom ? props.zoom : 13;
  const setZoom = props.setZoom ? props.setZoom : () => {};
  const markerFactory = props.markerFactory;
  const waitForPos = props.waitForPos;
  const showAccuracy = props.showAccuracy ? true : false;

  const [map, _setMap] = React.useState<LeafletMap | undefined>(undefined);
  const [isMapReady, setIsMapReady] = React.useState<boolean>(false);
  const [mapError, setMapError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const mapRef = useRef<LeafletMap | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Memoize map setup function to prevent unnecessary re-renders
  const setMap = useCallback((m: LeafletMap | null) => {
    if (!m) {
      console.warn('Map instance is null');
      setMapError('Failed to initialize map');
      setIsLoading(false);
      return;
    }

    try {
      _setMap(m);
      mapRef.current = m;
      logger.debug('Saved map in state');

      // Clear any previous errors
      setMapError(null);

      // Force map to invalidate size after a short delay to handle container sizing issues
      const invalidateMapSize = (): void => {
        if (m && m.invalidateSize && containerRef.current) {
          try {
            m.invalidateSize({ pan: false });
            logger.debug('Map size invalidated');
            setIsMapReady(true);
            setIsLoading(false);
          } catch (error) {
            console.warn('Error invalidating map size:', error);
            setMapError('Error resizing map');
          }
        }
      };

      // Multiple attempts to ensure map renders properly
      setTimeout(invalidateMapSize, 100);
      setTimeout(invalidateMapSize, 300);
      setTimeout(invalidateMapSize, 500);
    } catch (error) {
      console.error('Error setting up map:', error);
      setMapError('Failed to setup map');
      setIsLoading(false);
    }
  }, []);

  // Effect to handle window resize and re-invalidate map size
  useEffect(() => {
    const handleResize = (): void => {
      if (mapRef.current && mapRef.current.invalidateSize) {
        setTimeout(() => {
          mapRef.current?.invalidateSize({ pan: false });
        }, 100);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Effect to handle prop changes that might require map refresh
  useEffect(() => {
    if (map && map.invalidateSize) {
      setTimeout(() => {
        map.invalidateSize({ pan: false });
      }, 50);
    }
  }, [map, height, width, minHeight, minWidth]);

  // Effect to handle component mount - reset states on fresh load
  useEffect(() => {
    logger.debug('TimeSpace component mounted/refreshed');
    setIsMapReady(false);
    setMapError(null);
    setIsLoading(true);

    // Force a map refresh after a short delay if we have a map instance
    const refreshTimeout = setTimeout(() => {
      if (mapRef.current && mapRef.current.invalidateSize) {
        try {
          mapRef.current.invalidateSize({ pan: false });
          logger.debug('Forced map refresh on component mount');
        } catch (error) {
          console.warn('Error forcing map refresh:', error);
        }
      }
    }, 200);

    return () => clearTimeout(refreshTimeout);
  }, []);

  const stableMapKey = 'map-container';

  const filtered: BaseMapItem[] = [];
  logger.debug('TimeSpace received series:', series.length, 'items');
  series.map((item) => {
    if (
      (item.timestamp || item.positionTime) &&
      Number.isFinite(item.lat) &&
      Number.isFinite(item.lng)
    ) {
      logger.debug('TimeSpace adding item:', item.title, 'at', item.lat, item.lng);
      filtered.push(item);
    } else {
      logger.debug(
        'TimeSpace skipping item with title ' + item.title + ' - missing position or timestamp',
        {
          hasTimestamp: !!(item.timestamp || item.positionTime),
          hasLat: Number.isFinite(item.lat),
          hasLng: Number.isFinite(item.lng),
        }
      );
    }
    return 0;
  });
  series = filtered;
  logger.debug('TimeSpace filtered to:', series.length, 'items');

  const center: LatLng = props.center ?? [20, 0];
  logger.debug('Map Center:', center);

  // Fly to new center whenever the center prop changes after initial mount.
  // Dependencies use scalar lat/lng to avoid re-running on every render due to array identity.
  const centerLat = center[0];
  const centerLng = center[1];
  const isFirstFlyRef = useRef(true);
  useEffect(() => {
    if (isFirstFlyRef.current) {
      // Skip initial mount — MapContainer already positions via the center prop.
      isFirstFlyRef.current = false;
      return;
    }
    if (mapRef.current) {
      // Preserve the user's current zoom; only pan to the new position.
      mapRef.current.flyTo([centerLat, centerLng], mapRef.current.getZoom(), { duration: 0.6 });
    }
  }, [centerLat, centerLng]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tracks whether a programmatic alarm fly-to is in progress. Set to true immediately before
  // flyTo/flyToBounds and cleared by moveend. Only covers our own animations — not user drags —
  // so getBounds() is only skipped when it would return genuinely mid-flight values.
  const isFlyingRef = useRef(false);
  useEffect(() => {
    if (!map) return;
    const onMoveEnd = (): void => {
      isFlyingRef.current = false;
    };
    map.on('moveend', onMoveEnd);
    return () => {
      isFlyingRef.current = false;
      map.off('moveend', onMoveEnd);
    };
  }, [map]);

  // One-shot fit-bounds for geofence areas. Calls fitBounds with the given sw/ne and padding,
  // then immediately notifies the parent so the request is cleared.
  const fitBoundsRequest = props.fitBoundsRequest;
  useEffect(() => {
    if (!fitBoundsRequest || !mapRef.current) return;
    const { swNe, padding } = fitBoundsRequest;
    const [sw, ne] = swNe;
    mapRef.current.fitBounds(
      [
        [sw[0], sw[1]],
        [ne[0], ne[1]],
      ],
      {
        padding: [padding, padding],
        maxZoom: 18,
      }
    );
    props.onFitBoundsConsumed?.();
  }, [fitBoundsRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  // One-shot fly-to covering all alarm positions. Skipped if every position is already in view.
  // For a single device uses flyTo; for multiple uses flyToBounds so all are visible together.
  // Always calls onFlyConsumed (even on skip) so the parent resets flyToTarget to null, preventing
  // stale replay if TimeSpace remounts (e.g. user switches projection and back).
  const flyToTarget = props.flyToTarget;
  useEffect(() => {
    if (!flyToTarget || !mapRef.current) return;
    const map = mapRef.current;
    const { positions, force } = flyToTarget;

    // Skip when everything is already visible — except for forced requests
    // (explicit user clicks, e.g. the device status list), which must always center.
    if (!isFlyingRef.current && !force) {
      const currentBounds = map.getBounds();
      if (positions.every((pos) => currentBounds.contains(pos as L.LatLngExpression))) {
        props.onFlyConsumed?.();
        return;
      }
    }

    isFlyingRef.current = true;
    if (positions.length === 1) {
      map.flyTo(positions[0], ALARM_ZOOM, { duration: 0.8 });
    } else {
      const bounds = L.latLngBounds(positions as L.LatLngExpression[]);
      map.flyToBounds(bounds, { padding: [50, 50], maxZoom: ALARM_ZOOM, duration: 0.8 });
    }
    props.onFlyConsumed?.();
  }, [flyToTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  // Account-wide camera lock: clamp panning to the locked rectangle's bounds and
  // prevent zooming out past minZoom. Re-applied whenever the lock or map changes;
  // fully released (bounds + min zoom restored) when the lock is disabled or cleared.
  const cameraLock = props.cameraLock;
  useEffect(() => {
    if (!map) return;

    const active = cameraLock?.enabled && cameraLock.shape != null;
    if (active) {
      const [sw, ne] = getAreaBounds(cameraLock!.shape!);
      const bounds = L.latLngBounds(sw as L.LatLngExpression, ne as L.LatLngExpression);
      map.setMaxBounds(bounds);
      map.options.maxBoundsViscosity = 1.0; // hard wall — drags cannot leave the area
      if (cameraLock!.minZoom != null) {
        map.setMinZoom(cameraLock!.minZoom);
      }
      // Snap the current view inside the new constraints (covers views set before the lock loaded).
      map.panInsideBounds(bounds, { animate: false });
    } else {
      map.setMaxBounds(null as unknown as L.LatLngBoundsExpression);
      map.options.maxBoundsViscosity = 0.0;
      map.setMinZoom(0);
    }
  }, [map, cameraLock]);

  const MyZoomListener: React.FC = () => {
    const mapEvents = useMapEvents({
      zoomend: () => {
        setZoom(mapEvents.getZoom());
        logger.debug('New zoom level:', zoom);
      },
    });
    return null;
  };

  if (series.length > 0) {
    let oldestS =
      new Date(series[0].positionTime || series[0].timestamp || new Date()).getTime() / 1000;
    let newestS = oldestS;
    series.forEach((item) => {
      const s = new Date(item.positionTime || item.timestamp || new Date()).getTime() / 1000;
      if (s < oldestS) oldestS = s;
      if (s > newestS) newestS = s;
    });

    ageForMaxOpacityH = Number.isInteger(props.maxAge)
      ? (props.maxAge as number) / 3600.0
      : (nowS - oldestS) / 3600.0;
    if (ageForMaxOpacityH < 0.1) ageForMaxOpacityH = 0.1;
  }

  return (
    <div
      ref={containerRef}
      data-testid="timespace-wrapper"
      style={{
        fontSize: '3vmin',
        height: minHeight,
        width: minWidth,
        top,
        left: 0,
        position: 'relative',
      }}
    >
      {/* Loading overlay */}
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255,255,255,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            fontSize: '2vmin',
          }}
        >
          Loading map...
        </div>
      )}

      {/* Error display */}
      {mapError && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: '#ffeeee',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            fontSize: '2vmin',
            color: '#cc0000',
          }}
        >
          Map Error: {mapError}
          <button onClick={() => window.location.reload()} style={{ marginLeft: '10px' }}>
            Reload
          </button>
        </div>
      )}

      {/* Status overlays — shown on top of the map */}
      {waitForPos && (
        <div
          style={{
            position: 'absolute',
            top: '1rem',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(255,255,255,0.85)',
            padding: '0.5rem 1rem',
            borderRadius: 4,
            fontSize: '1.6vmin',
            zIndex: 1000,
            pointerEvents: 'none',
          }}
        >
          Waiting for position…
        </div>
      )}
      {series.length === 0 && !waitForPos && (
        <div
          style={{
            position: 'absolute',
            // Sits below the Active/Alarming status boxes in the top-centre strip
            top: '4rem',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(255,255,255,0.85)',
            padding: '0.5rem 1rem',
            borderRadius: 4,
            fontSize: '1.6vmin',
            zIndex: 1000,
            pointerEvents: 'none',
          }}
        >
          No positions to display.
        </div>
      )}

      <MapContainer
        key={stableMapKey}
        style={{
          height: minHeight,
          width: minWidth,
          left: 0,
          minHeight: '300px',
        }}
        center={center}
        zoom={zoom}
        // react-leaflet types whenReady as () => void, but Leaflet passes {target: map}
        whenReady={
          ((event: { target: LeafletMap }) => setMap(event.target)) as unknown as () => void
        }
        zoomControl={false}
        className="leaflet-container"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {/* Extra layers (e.g. GeofenceAreas) rendered below device markers */}
        {props.children}
        {map &&
          isMapReady &&
          series.length > 0 &&
          markerFactory(
            map,
            series,
            props.selected ?? null,
            nowS,
            ageForMaxOpacityH,
            opacityScale,
            maxOpacity,
            zoom,
            showAccuracy
          )}
        <MyZoomListener />
      </MapContainer>
    </div>
  );
};
